/**
 * Trivia AI Opponent
 *
 * Simulates an AI player answering trivia questions with configurable
 * difficulty levels. Each difficulty has different accuracy rates and
 * response times.
 *
 * - Easy: 40% accuracy, slow response (8-12s)
 * - Medium: 65% accuracy, moderate response (4-8s)
 * - Hard: 85% accuracy, fast response (2-4s)
 */

import { TriviaQuestion, TriviaDifficulty } from './types';

interface AIConfig {
  accuracy: number; // 0-1 probability of correct answer
  minResponseTime: number; // milliseconds
  maxResponseTime: number; // milliseconds
}

const DIFFICULTY_CONFIG: Record<TriviaDifficulty, AIConfig> = {
  easy: {
    accuracy: 0.4,
    minResponseTime: 8000,
    maxResponseTime: 12000,
  },
  medium: {
    accuracy: 0.65,
    minResponseTime: 4000,
    maxResponseTime: 8000,
  },
  hard: {
    accuracy: 0.85,
    minResponseTime: 2000,
    maxResponseTime: 4000,
  },
};

export class TriviaAI {
  private difficulty: TriviaDifficulty;
  private config: AIConfig;

  constructor(difficulty: TriviaDifficulty = 'medium') {
    this.difficulty = difficulty;
    this.config = DIFFICULTY_CONFIG[difficulty];
  }

  /**
   * Get the AI's difficulty level
   */
  getDifficulty(): TriviaDifficulty {
    return this.difficulty;
  }

  /**
   * Set the AI's difficulty level
   */
  setDifficulty(difficulty: TriviaDifficulty): void {
    this.difficulty = difficulty;
    this.config = DIFFICULTY_CONFIG[difficulty];
  }

  /**
   * Determine if the AI will answer correctly based on difficulty
   */
  willAnswerCorrectly(): boolean {
    return Math.random() < this.config.accuracy;
  }

  /**
   * Get the AI's response time for a question (in milliseconds)
   */
  getResponseTime(): number {
    const { minResponseTime, maxResponseTime } = this.config;
    return minResponseTime + Math.random() * (maxResponseTime - minResponseTime);
  }

  /**
   * Generate an AI answer for the given question.
   * Returns the selected option string.
   */
  generateAnswer(question: TriviaQuestion): string {
    const shouldBeCorrect = this.willAnswerCorrectly();

    if (shouldBeCorrect) {
      return question.correctAnswer;
    }

    // Pick a random wrong answer
    const wrongOptions = question.options.filter(opt => opt !== question.correctAnswer);
    if (wrongOptions.length === 0) {
      return question.correctAnswer; // Fallback if somehow no wrong options
    }
    const randomIndex = Math.floor(Math.random() * wrongOptions.length);
    return wrongOptions[randomIndex];
  }

  /**
   * Schedule an AI answer with realistic timing.
   * Returns a promise that resolves with the selected answer after the response delay.
   */
  async answerQuestion(question: TriviaQuestion): Promise<{ answer: string; responseTime: number }> {
    const responseTime = this.getResponseTime();
    const answer = this.generateAnswer(question);

    return new Promise(resolve => {
      setTimeout(() => {
        resolve({ answer, responseTime });
      }, responseTime);
    });
  }

  /**
   * Get the AI's answer synchronously (for testing or immediate use)
   */
  getImmediateAnswer(question: TriviaQuestion): { answer: string; responseTime: number } {
    const responseTime = this.getResponseTime();
    const answer = this.generateAnswer(question);
    return { answer, responseTime };
  }
}
